import { ErroApi } from './erros';
import { novoId } from './id';

/** Contexto enviado em todo request (07 §2.8): X-Persona-Empresa, X-Persona-Filial, X-Persona-Data-Base. */
export interface ContextoRequisicao {
  empresa?: string;
  filial?: string;
  dataBase?: string;
}

let contexto: ContextoRequisicao = {};
let aoPerderSessao: (() => void) | null = null;
let aoMudarLicenca: ((estado: string) => void) | null = null;

export function definirContexto(c: ContextoRequisicao) {
  contexto = c;
}

export function lerContexto(): ContextoRequisicao {
  return contexto;
}

export function aoSessaoExpirar(f: () => void) {
  aoPerderSessao = f;
}

export function aoEstadoLicenca(f: (estado: string) => void) {
  aoMudarLicenca = f;
}

export interface Envelope<T> {
  ok: boolean;
  dados: T;
  erro: { codigo: string; mensagem: string; campos: Record<string, string> | null } | null;
  meta: { traceId?: string; total?: number; totalEstimado?: boolean; proximoCursor?: string | null; [k: string]: unknown } | null;
}

export interface Opcoes {
  metodo?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  corpo?: unknown;
  sinal?: AbortSignal;
  /** Chave de idempotência explícita (retry da mesma operação); POST sem ela recebe uma nova. */
  chaveIdempotencia?: string;
  /** POST que só lê (ex.: prévia de impostos): vai sem chave de idempotência, o servidor não guarda resposta para repetir. */
  consulta?: boolean;
  cabecalhos?: Record<string, string>;
}

export async function requisitar<T>(caminho: string, o: Opcoes = {}): Promise<{ dados: T; meta: Envelope<T>['meta']; resposta: Response }> {
  const metodo = o.metodo ?? 'GET';
  const h: Record<string, string> = { Accept: 'application/json', ...o.cabecalhos };
  if (contexto.empresa) h['X-Persona-Empresa'] = contexto.empresa;
  if (contexto.filial) h['X-Persona-Filial'] = contexto.filial;
  if (contexto.dataBase) h['X-Persona-Data-Base'] = contexto.dataBase;
  if (metodo !== 'GET') h['X-Persona-Csrf'] = '1';
  if (metodo === 'POST' && !o.consulta) h['Idempotency-Key'] = o.chaveIdempotencia ?? novoId();
  const arquivo = o.corpo instanceof FormData;
  if (o.corpo !== undefined && !arquivo) h['Content-Type'] = 'application/json';

  let resposta: Response;
  try {
    resposta = await fetch(`/api/v1${caminho}`, { method: metodo, headers: h, body: o.corpo === undefined ? undefined : arquivo ? (o.corpo as FormData) : JSON.stringify(o.corpo), credentials: 'same-origin', signal: o.sinal });
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ErroApi(0, 'SEM_CONEXAO', 'Sem conexão com o servidor — tente de novo em instantes');
  }
  const licenca = resposta.headers.get('X-Persona-Licenca-Estado');
  if (licenca) aoMudarLicenca?.(licenca);
  if (resposta.status === 304) return { dados: undefined as T, meta: null, resposta };

  let env: Envelope<T> | null = null;
  try {
    env = (await resposta.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!resposta.ok || !env?.ok) {
    if (resposta.status === 401 && !caminho.startsWith('/auth/entrar')) aoPerderSessao?.();
    throw new ErroApi(resposta.status, env?.erro?.codigo ?? `HTTP_${resposta.status}`, env?.erro?.mensagem ?? `O servidor respondeu ${resposta.status}`,
      env?.erro?.campos ?? null, env?.meta?.traceId);
  }
  return { dados: env.dados, meta: env.meta, resposta };
}

/**
 * Baixa o arquivo que um endpoint GET devolve (XML, PDF): mesmo contexto e sessão das demais chamadas. Erro vem no envelope JSON de sempre.
 * O nome é o do `Content-Disposition`; sem ele, `nomePadrao`.
 */
export async function baixarArquivo(caminho: string, nomePadrao: string): Promise<string> {
  const h: Record<string, string> = { Accept: '*/*' };
  if (contexto.empresa) h['X-Persona-Empresa'] = contexto.empresa;
  if (contexto.filial) h['X-Persona-Filial'] = contexto.filial;
  if (contexto.dataBase) h['X-Persona-Data-Base'] = contexto.dataBase;
  let resposta: Response;
  try {
    resposta = await fetch(`/api/v1${caminho}`, { headers: h, credentials: 'same-origin' });
  } catch {
    throw new ErroApi(0, 'SEM_CONEXAO', 'Sem conexão com o servidor — tente de novo em instantes');
  }
  if (!resposta.ok) {
    let env: Envelope<unknown> | null = null;
    try {
      env = (await resposta.json()) as Envelope<unknown>;
    } catch {
      env = null;
    }
    if (resposta.status === 401) aoPerderSessao?.();
    throw new ErroApi(resposta.status, env?.erro?.codigo ?? `HTTP_${resposta.status}`, env?.erro?.mensagem ?? `O servidor respondeu ${resposta.status}`,
      env?.erro?.campos ?? null, env?.meta?.traceId);
  }
  const disposicao = resposta.headers.get('Content-Disposition') ?? '';
  const nome = /filename\*=UTF-8''([^;]+)/i.exec(disposicao)?.[1] ?? /filename="?([^";]+)"?/i.exec(disposicao)?.[1] ?? nomePadrao;
  const url = URL.createObjectURL(await resposta.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = decodeURIComponent(nome);
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return link.download;
}

export const api = {
  get: <T>(c: string, sinal?: AbortSignal) => requisitar<T>(c, { sinal }).then((r) => r.dados),
  post: <T>(c: string, corpo?: unknown, o: Omit<Opcoes, 'metodo' | 'corpo'> = {}) => requisitar<T>(c, { ...o, metodo: 'POST', corpo }).then((r) => r.dados),
  put: <T>(c: string, corpo?: unknown) => requisitar<T>(c, { metodo: 'PUT', corpo }).then((r) => r.dados),
  delete: <T>(c: string) => requisitar<T>(c, { metodo: 'DELETE' }).then((r) => r.dados),
};

/** base64url do JSON de filtros (01 §6.1). */
export function codificarFiltros(filtros: { campo: string; op: string; valor: unknown }[]): string {
  const bytes = new TextEncoder().encode(JSON.stringify(filtros));
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
